import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  Annotation,
  IssueCategory,
  LabelLayoutMap,
  LabelLayoutZone,
} from '../../api/client';

const ISSUE_CATEGORIES: { value: IssueCategory; label: string }[] = [
  { value: 'position', label: 'Position' },
  { value: 'size', label: 'Size' },
  { value: 'missing', label: 'Missing' },
  { value: 'wrong_content', label: 'Wrong content' },
  { value: 'barcode', label: 'Barcode' },
  { value: 'spacing', label: 'Spacing' },
  { value: 'font', label: 'Font' },
  { value: 'other', label: 'Other' },
];

const PIN_SIZE = 0.08;

export interface LabelPreviewAnnotatorProps {
  previewUrl: string;
  layoutMap: LabelLayoutMap | null;
  annotations: Annotation[];
  onChangeAnnotations: (annotations: Annotation[]) => void;
  showOverlay?: boolean;
  onToggleOverlay?: (show: boolean) => void;
  /** Fired when a pin is saved so parent can append overall comment */
  onAnnotationCommitted?: (annotation: Annotation, zoneLabel?: string) => void;
}

function clamp01(n: number): number {
  return Math.min(1, Math.max(0, n));
}

function zoneContainsNormalized(
  zone: LabelLayoutZone,
  xNorm: number,
  yNorm: number,
): boolean {
  return (
    xNorm >= zone.xNorm &&
    xNorm <= zone.xNorm + zone.wNorm &&
    yNorm >= zone.yNorm &&
    yNorm <= zone.yNorm + zone.hNorm
  );
}

function newAnnotationId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `ann-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

interface DraftPopover {
  mode: 'create' | 'edit';
  annotationId?: string;
  xNorm: number;
  yNorm: number;
  /** Pixel position relative to wrapper for popover */
  leftPx: number;
  topPx: number;
  zoneId?: string;
  zoneLabel?: string;
  fieldName?: string;
  targetType: Annotation['targetType'];
  region?: { xNorm: number; yNorm: number; wNorm: number; hNorm: number };
  category: IssueCategory;
  comment: string;
}

/** Field hit target size as fraction of label (min ~readable chip). */
const FIELD_HIT = 0.1;

/** Short display name for a field path (shipFrom.name → shipFrom). */
function shortFieldName(name?: string): string {
  if (!name) return 'field';
  return name.split('.')[0] || name;
}

export function LabelPreviewAnnotator({
  previewUrl,
  layoutMap,
  annotations,
  onChangeAnnotations,
  showOverlay = true,
  onToggleOverlay,
  onAnnotationCommitted,
}: LabelPreviewAnnotatorProps) {
  const imgRef = useRef<HTMLImageElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const commentInputRef = useRef<HTMLTextAreaElement>(null);
  const [hoveredZoneId, setHoveredZoneId] = useState<string | null>(null);
  const [hoveredField, setHoveredField] = useState<string | null>(null);
  const [draft, setDraft] = useState<DraftPopover | null>(null);
  const [showFields, setShowFields] = useState(true);

  const zones = layoutMap?.zones ?? [];
  const fields = layoutMap?.fields ?? [];
  const hasZones = zones.length > 0;

  const zoneById = useMemo(() => {
    const map = new Map<string, LabelLayoutZone>();
    for (const z of zones) map.set(z.id, z);
    return map;
  }, [zones]);

  useEffect(() => {
    if (draft && commentInputRef.current) {
      commentInputRef.current.focus();
    }
  }, [draft]);

  const openCreateAtClick = useCallback(
    (e: React.MouseEvent<HTMLImageElement>) => {
      const img = imgRef.current;
      const wrap = wrapRef.current;
      if (!img || !wrap) return;

      const rect = img.getBoundingClientRect();
      const wrapRect = wrap.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) return;

      const xNorm = clamp01((e.clientX - rect.left) / rect.width);
      const yNorm = clamp01((e.clientY - rect.top) / rect.height);

      let targetType: Annotation['targetType'] = 'region';
      let zoneId: string | undefined;
      let zoneLabel: string | undefined;
      let region = {
        xNorm: clamp01(xNorm - PIN_SIZE / 2),
        yNorm: clamp01(yNorm - PIN_SIZE / 2),
        wNorm: PIN_SIZE,
        hNorm: PIN_SIZE,
      };

      if (layoutMap) {
        const hit = zones.find((z) => zoneContainsNormalized(z, xNorm, yNorm));
        if (hit) {
          targetType = 'zone';
          zoneId = hit.id;
          zoneLabel = hit.label;
          region = {
            xNorm: hit.xNorm,
            yNorm: hit.yNorm,
            wNorm: hit.wNorm,
            hNorm: hit.hNorm,
          };
        }
      }

      setDraft({
        mode: 'create',
        xNorm,
        yNorm,
        leftPx: e.clientX - wrapRect.left,
        topPx: e.clientY - wrapRect.top,
        zoneId,
        zoneLabel,
        targetType,
        region,
        category: 'other',
        comment: '',
      });
    },
    [layoutMap, zones],
  );

  const openFieldComment = (
    field: { name?: string; xNorm: number; yNorm: number },
    e: React.MouseEvent,
  ) => {
    e.stopPropagation();
    const wrap = wrapRef.current;
    const img = imgRef.current;
    if (!wrap || !img) return;
    const wrapRect = wrap.getBoundingClientRect();
    const rect = img.getBoundingClientRect();
    const half = FIELD_HIT / 2;
    setDraft({
      mode: 'create',
      xNorm: field.xNorm,
      yNorm: field.yNorm,
      leftPx: rect.left - wrapRect.left + field.xNorm * rect.width,
      topPx: rect.top - wrapRect.top + field.yNorm * rect.height,
      fieldName: field.name,
      zoneLabel: field.name
        ? `Field ${shortFieldName(field.name)}`
        : 'Field',
      targetType: 'field',
      region: {
        xNorm: clamp01(field.xNorm - half),
        yNorm: clamp01(field.yNorm - half),
        wNorm: FIELD_HIT,
        hNorm: FIELD_HIT,
      },
      category: 'other',
      comment: '',
    });
  };

  const openEditPin = (ann: Annotation, e: React.MouseEvent) => {
    e.stopPropagation();
    const wrap = wrapRef.current;
    const img = imgRef.current;
    if (!wrap || !img) return;
    const wrapRect = wrap.getBoundingClientRect();
    const rect = img.getBoundingClientRect();
    const cx = ann.xNorm + (ann.wNorm ?? PIN_SIZE) / 2;
    const cy = ann.yNorm + (ann.hNorm ?? PIN_SIZE) / 2;
    setDraft({
      mode: 'edit',
      annotationId: ann.id,
      xNorm: cx,
      yNorm: cy,
      leftPx: rect.left - wrapRect.left + cx * rect.width,
      topPx: rect.top - wrapRect.top + cy * rect.height,
      zoneId: ann.zoneId,
      zoneLabel: ann.fieldName
        ? `Field ${ann.fieldName}`
        : ann.zoneId
          ? zoneById.get(ann.zoneId)?.label
          : undefined,
      fieldName: ann.fieldName,
      targetType: ann.targetType,
      region: {
        xNorm: ann.xNorm,
        yNorm: ann.yNorm,
        wNorm: ann.wNorm ?? PIN_SIZE,
        hNorm: ann.hNorm ?? PIN_SIZE,
      },
      category: ann.category,
      comment: ann.comment,
    });
  };

  const commitDraft = () => {
    if (!draft) return;
    const comment = draft.comment.trim();
    if (!comment) return;

    const region = draft.region ?? {
      xNorm: clamp01(draft.xNorm - PIN_SIZE / 2),
      yNorm: clamp01(draft.yNorm - PIN_SIZE / 2),
      wNorm: PIN_SIZE,
      hNorm: PIN_SIZE,
    };

    if (draft.mode === 'edit' && draft.annotationId) {
      const updated = annotations.map((a) =>
        a.id === draft.annotationId
          ? {
              ...a,
              comment,
              category: draft.category,
              targetType: draft.targetType,
              zoneId: draft.zoneId,
              fieldName: draft.fieldName,
              xNorm: region.xNorm,
              yNorm: region.yNorm,
              wNorm: region.wNorm,
              hNorm: region.hNorm,
            }
          : a,
      );
      onChangeAnnotations(updated);
      const saved = updated.find((a) => a.id === draft.annotationId);
      if (saved) {
        onAnnotationCommitted?.(
          saved,
          draft.fieldName ? `Field ${draft.fieldName}` : draft.zoneLabel,
        );
      }
    } else {
      const pin: Annotation = {
        id: newAnnotationId(),
        targetType: draft.targetType,
        zoneId: draft.zoneId,
        fieldName: draft.fieldName,
        xNorm: region.xNorm,
        yNorm: region.yNorm,
        wNorm: region.wNorm,
        hNorm: region.hNorm,
        category: draft.category,
        comment,
      };
      onChangeAnnotations([...annotations, pin]);
      onAnnotationCommitted?.(
        pin,
        draft.fieldName ? `Field ${draft.fieldName}` : draft.zoneLabel,
      );
    }
    setDraft(null);
  };

  const removeAnnotation = (id: string) => {
    onChangeAnnotations(annotations.filter((a) => a.id !== id));
    if (draft?.annotationId === id) setDraft(null);
  };

  const hoveredZone = hoveredZoneId ? zoneById.get(hoveredZoneId) : null;
  const sourceLabel =
    layoutMap?.zoneSource === 'stored' || layoutMap?.zoneSource === 'manual'
      ? 'Template map'
      : layoutMap?.zoneSource === 'onboarding-zones'
        ? 'Onboarding zones'
        : layoutMap?.zoneSource === 'zpl-derived'
          ? 'From ZPL'
          : layoutMap?.zoneSource === 'gs1-4x6'
            ? 'GS1 estimate'
            : layoutMap?.zoneSource
              ? 'Estimated grid'
              : null;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="space-y-1 min-w-0">
          <p className="text-sm text-gray-700">
            <strong>Click to comment</strong> — blue boxes are layout zones; amber tags are
            individual data fields (finer feedback).
          </p>
          <p className="text-xs text-gray-500">
            Hover a zone for its name. Click an amber field tag to pin feedback on that field
            (not just the region).
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {sourceLabel && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 border border-gray-200">
              {sourceLabel}
            </span>
          )}
          {layoutMap && onToggleOverlay && (
            <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none">
              <input
                type="checkbox"
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                checked={showOverlay}
                onChange={(e) => onToggleOverlay(e.target.checked)}
              />
              Zones
            </label>
          )}
          {fields.length > 0 && (
            <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none">
              <input
                type="checkbox"
                className="rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                checked={showFields}
                onChange={(e) => setShowFields(e.target.checked)}
              />
              Field tags
            </label>
          )}
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-4 text-xs text-gray-600">
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block w-4 h-3 rounded-sm border-2 border-blue-400 bg-blue-400/15" />
          Zone (layout area) — click
        </span>
        {fields.length > 0 && (
          <span className="inline-flex items-center gap-1.5">
            <span className="inline-flex items-center rounded border border-amber-600 bg-amber-200 px-1.5 py-0.5 text-[10px] font-semibold text-amber-950">
              field
            </span>
            Field tag (element) — click
          </span>
        )}
      </div>

      <div className="flex justify-center items-center bg-gray-50 rounded-lg p-4">
        <div ref={wrapRef} className="relative inline-block max-w-full">
          <img
            ref={imgRef}
            src={previewUrl}
            alt="Label Preview"
            className="max-w-full max-h-[600px] w-auto h-auto border rounded shadow cursor-crosshair block"
            onClick={openCreateAtClick}
            draggable={false}
          />

          {showOverlay && hasZones && (
            <div className="absolute inset-0 pointer-events-none">
              {zones.map((zone) => {
                const isHovered = hoveredZoneId === zone.id;
                return (
                  <div
                    key={zone.id}
                    className={`absolute border-2 rounded-sm transition-colors ${
                      isHovered
                        ? 'border-primary-500 bg-primary-500/20'
                        : 'border-blue-400/70 bg-blue-400/10'
                    }`}
                    style={{
                      left: `${zone.xNorm * 100}%`,
                      top: `${zone.yNorm * 100}%`,
                      width: `${zone.wNorm * 100}%`,
                      height: `${zone.hNorm * 100}%`,
                      pointerEvents: 'auto',
                    }}
                    onMouseEnter={() => setHoveredZoneId(zone.id)}
                    onMouseLeave={() => setHoveredZoneId(null)}
                    onClick={(e) => {
                      e.stopPropagation();
                      // Synthesize click at zone center on image
                      const img = imgRef.current;
                      const wrap = wrapRef.current;
                      if (!img || !wrap) return;
                      const rect = img.getBoundingClientRect();
                      const wrapRect = wrap.getBoundingClientRect();
                      const cx = zone.xNorm + zone.wNorm / 2;
                      const cy = zone.yNorm + zone.hNorm / 2;
                      setDraft({
                        mode: 'create',
                        xNorm: cx,
                        yNorm: cy,
                        leftPx: rect.left - wrapRect.left + cx * rect.width,
                        topPx: rect.top - wrapRect.top + cy * rect.height,
                        zoneId: zone.id,
                        zoneLabel: zone.label,
                        targetType: 'zone',
                        region: {
                          xNorm: zone.xNorm,
                          yNorm: zone.yNorm,
                          wNorm: zone.wNorm,
                          hNorm: zone.hNorm,
                        },
                        category: 'other',
                        comment: '',
                      });
                    }}
                    title={`${zone.label} (${zone.id})`}
                  />
                );
              })}

            </div>
          )}

          {/* Field tags — clickable element-level feedback (larger than a pin) */}
          {showFields && fields.length > 0 && (
            <div className="absolute inset-0 pointer-events-none">
              {fields.map((field, idx) => {
                const fullName = field.name || `field-${idx + 1}`;
                const short = shortFieldName(field.name);
                const display =
                  short.length > 14 ? `${short.slice(0, 12)}…` : short;
                const key = `${fullName}-${idx}`;
                const isHov = hoveredField === key;
                return (
                  <button
                    key={key}
                    type="button"
                    className={`absolute pointer-events-auto z-[5] flex items-center gap-1 rounded-full border-2 pl-1 pr-2 py-1 text-[11px] font-semibold shadow-md transition-all max-w-[32%] truncate cursor-pointer ${
                      isHov
                        ? 'bg-amber-400 border-amber-800 text-amber-950 scale-110 ring-2 ring-amber-300'
                        : 'bg-amber-200 border-amber-700 text-amber-950 hover:bg-amber-300'
                    }`}
                    style={{
                      // Anchor near FO origin (top-left of field content), nudge into content
                      left: `${field.xNorm * 100}%`,
                      top: `${field.yNorm * 100}%`,
                      transform: 'translate(2px, 2px)',
                      minHeight: 26,
                      minWidth: 52,
                    }}
                    title={`Click to comment on field: ${fullName}`}
                    aria-label={`Comment on field ${fullName}`}
                    onMouseEnter={() => setHoveredField(key)}
                    onMouseLeave={() => setHoveredField(null)}
                    onClick={(e) => openFieldComment(field, e)}
                  >
                    <span
                      className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-amber-700 text-[9px] font-bold text-white"
                      aria-hidden
                    >
                      ·
                    </span>
                    {display}
                  </button>
                );
              })}
            </div>
          )}

          <div className="absolute inset-0 pointer-events-none">
            {annotations.map((ann, index) => {
              const w = ann.wNorm ?? PIN_SIZE;
              const h = ann.hNorm ?? PIN_SIZE;
              return (
                <button
                  key={ann.id}
                  type="button"
                  className="absolute border-2 border-red-500 bg-red-500/25 rounded-sm flex items-start justify-start pointer-events-auto cursor-pointer"
                  style={{
                    left: `${ann.xNorm * 100}%`,
                    top: `${ann.yNorm * 100}%`,
                    width: `${w * 100}%`,
                    height: `${h * 100}%`,
                  }}
                  onClick={(e) => openEditPin(ann, e)}
                  title={ann.comment || `Pin ${index + 1}`}
                >
                  <span className="text-[10px] font-bold leading-none bg-red-500 text-white px-1 py-0.5 rounded-br">
                    {index + 1}
                  </span>
                </button>
              );
            })}
          </div>

          {showOverlay && hoveredZone && !draft && (
            <div className="absolute left-2 top-2 z-10 max-w-[80%] pointer-events-none bg-gray-900/90 text-white text-xs rounded px-2 py-1 shadow">
              <span className="font-medium">{hoveredZone.label}</span>
              <span className="text-gray-300 ml-1">({hoveredZone.id})</span>
            </div>
          )}

          {/* In-place comment popover */}
          {draft && (
            <div
              className="absolute z-20 w-72 bg-white border border-gray-200 rounded-lg shadow-xl p-3 space-y-2"
              style={{
                left: Math.min(Math.max(8, draft.leftPx - 20), 280),
                top: Math.min(draft.topPx + 12, 400),
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="text-xs font-semibold text-gray-800">
                {draft.targetType === 'field' && draft.fieldName
                  ? `Field: ${draft.fieldName}`
                  : draft.zoneLabel
                    ? `Zone ${draft.zoneId ?? ''} — ${draft.zoneLabel}`.replace(
                        /Zone\s+—/,
                        'Zone',
                      )
                    : draft.mode === 'edit'
                      ? 'Edit pin'
                      : 'Comment on region'}
              </div>
              <div>
                <label className="label text-xs">Category</label>
                <select
                  className="input text-sm"
                  value={draft.category}
                  onChange={(e) =>
                    setDraft({ ...draft, category: e.target.value as IssueCategory })
                  }
                >
                  {ISSUE_CATEGORIES.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label text-xs">Comment</label>
                <textarea
                  ref={commentInputRef}
                  className="input text-sm"
                  rows={3}
                  value={draft.comment}
                  onChange={(e) => setDraft({ ...draft, comment: e.target.value })}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                      e.preventDefault();
                      commitDraft();
                    }
                    if (e.key === 'Escape') setDraft(null);
                  }}
                  placeholder="What looks wrong?"
                />
              </div>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  className="btn btn-secondary text-sm"
                  onClick={() => setDraft(null)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary text-sm"
                  disabled={!draft.comment.trim()}
                  onClick={commitDraft}
                >
                  Save
                </button>
              </div>
              <p className="text-[10px] text-gray-400">Ctrl+Enter to save · Esc to cancel</p>
            </div>
          )}
        </div>
      </div>

      {annotations.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-sm font-semibold text-gray-900">
            Pins ({annotations.length})
          </h3>
          <ul className="space-y-2">
            {annotations.map((ann, index) => (
              <li
                key={ann.id}
                className="flex items-start justify-between gap-2 border border-gray-200 rounded-lg p-2 bg-white text-sm"
              >
                <div>
                  <span className="inline-flex w-5 h-5 items-center justify-center rounded-full bg-red-500 text-white text-xs font-bold mr-2">
                    {index + 1}
                  </span>
                  <span className="font-medium text-gray-800">
                    {ann.zoneId
                      ? `Zone ${ann.zoneId}${
                          zoneById.get(ann.zoneId)?.label
                            ? ` — ${zoneById.get(ann.zoneId)!.label}`
                            : ''
                        }`
                      : 'Region'}
                  </span>
                  <span className="text-gray-500 ml-2">({ann.category})</span>
                  <p className="text-gray-700 mt-0.5 ml-7">{ann.comment}</p>
                </div>
                <button
                  type="button"
                  className="text-red-600 text-sm hover:text-red-800 shrink-0"
                  onClick={() => removeAnnotation(ann.id)}
                >
                  Delete
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
